import React, {
  forwardRef,
  useRef,
  useImperativeHandle,
  useState,
} from 'react';
import { uuid } from '@helpers/utils';
import { AMAPKEY } from '@helpers/Constants';
import { Map, APILoader } from '@uiw/react-amap';

import { IMapOptionProps, IMapPoint } from './types';
import { sortDistanceData } from './mapUtils';

//新版本 必须在加载地图前配置密钥
window._AMapSecurityConfig = {
  securityJsCode: '1fca48292221b8ec848bd97c20c6231a',
};

const AMapContent = (props: IMapOptionProps, ref: any) => {
  const {
    center,
    zoom,
    minZoom,
    maxZoom,
    enablePinchToZoom = true,
    enableDoubleClickZoom = true,
    onZoomEnd = null,
    onTouchStart,
  } = props;
  const mapRef = useRef<any>(null);
  const mouseToolRef = useRef<any>(null);
  const drawType = useRef('');
  const [isFirstLoaded, setFirstLoaded] = useState(false);
  const [mapCenter] = useState([center.lng, center.lat]);

  useImperativeHandle(ref, () => ({
    zoomIn,
    zoomOut,
    getMapRef: () => mapRef.current.map,
    getZoom,
    setViewport,
    getLocation,
    getOverlays,
    clearOverlays,
    clearOverlayByType,
    removeOverlay,
    addOverlay,
    createPoint,
    createPolyline,
    setCenter,
    getCenter,
    disableDragging,
    enableDragging,
    addListener,
    removeListener,
    getLocationPois,
    locationSearch,
    setZoomAndCenter,
    setZoom,
    initMouseTool,
    mouseDraw,
    mouseToolClose,
    setOverlayStyle
  }));

  const initMouseTool = (callback: (data: any) => void) => {
    const instanceMap = mapRef.current.map;
    if(!mouseToolRef.current) {
      mouseToolRef.current = new (AMap as any).MouseTool(instanceMap);
    }
    mouseToolRef.current?.on('draw', (e: any) => {
      let data = null;
      if(drawType.current === 'circle') {
        data = {
          type: drawType.current,
          path:  [e.obj._opts.center.lng, e.obj._opts.center.lat],
          radius: e.obj._opts.radius
        }
      }

      if(drawType.current === 'polygon') {
        data = {
          type: drawType.current,
          path: e.obj._opts.path,
        }
      }
      callback && callback(data)
    })
  }

  const mouseToolClose = () => {
    mouseToolRef.current.close(false);
  }

  const mouseDraw = (type: string, options: {[key: string]: any}) => {
    drawType.current = type;
    if(type === 'circle') {
      mouseToolRef.current.circle(options);
    }

    if(type === 'polygon') {
      mouseToolRef.current.polygon(options);
    }
  }

  const setOverlayStyle = (styles: {
    fillColor?: string;
    strokeColor?: string;
  }) => {
    const currentOverlay = getOverlays()[0];
    currentOverlay?.setOptions(styles);
  }

  const getCenter = () => {
    const instanceMap = mapRef.current.map;
    const center = instanceMap.getCenter();
    return center;
  }
  const getLocationPois = async (position: IMapPoint, r: number) => {
    const geocoder: any = new AMap.Geocoder({
      radius: 1000, //以已知坐标为中心点，radius为半径，返回范围内兴趣点和道路信息
      extensions: 'all', //返回地址描述以及附近兴趣点和道路信息，默认“base”
    });
    return new Promise(resolve => {
      geocoder.getAddress(
        new AMap.LngLat(position.lng, position.lat),
        function(status: string, res: any) {
          const { regeocode: result } = res;
          const currentPoint = {
            ...position,
            address: result.formattedAddress || '位置',
            title: result.formattedAddress,
            distance: 0,
            uniqueId: uuid(),
          };
          let list = (result as any)?.pois || [];
          list = list.slice(0, 10).map((item: any, index: any) => {
            return {
              title: item.name,
              address: item.address,
              uniqueId: uuid(),
              distance: item.distance,
              lng: item.location.lng,
              lat: item.location.lat
            };
          });
          list = sortDistanceData(list);
          if( list[0].distance === 0 ) {
            resolve(list);
          } else {
            list.unshift(currentPoint);
            resolve(list);
          }
        },
        { poiRadius: r },
      );
    });
  };

  const locationSearch = (value: string, center: IMapPoint) => {
    return new Promise((resolve, reject) => {
      const local = new (AMap as any).PlaceSearch({
        pageSize: 100
      });
      local.search(value, (status: any, results: any) => {
        let list = results?.poiList?.pois || [];
        list = list.map((item: any, index: any) => {
          return {
            title: item.name,
            address: item.address,
            uniqueId: uuid(),
            lng: item.point.lng,
            lat: item.point.lat,
            distance: (AMap as any).GeometryUtil.distance([center.lng, center.lat], [item.location.lng, item.location.lat])
          };
        });
        list = sortDistanceData(list);
        resolve(list)
      });
    });
  };

  const enableDragging = () => {
    const instanceMap = mapRef.current.map;
    instanceMap.setStatus({
      dragEnable: true,
    });
  };
  const disableDragging = () => {
    const instanceMap = mapRef.current.map;
    instanceMap.setStatus({
      dragEnable: false,
    });
  };

  const addListener = (eventName: string, callback: () => void) => {
    const instanceMap = mapRef.current.map;
    instanceMap.on(eventName, callback);
  };

  const removeListener = (eventName: string, callback: () => void) => {
    const instanceMap = mapRef.current.map;
    instanceMap.off(eventName, callback);
  };

  const setViewport = (viewport?: { points: any[]; margins: number[] }) => {
    const instanceMap = mapRef.current.map;
    const allOverlay = instanceMap.getAllOverlays();
    instanceMap.setFitView(allOverlay, true, viewport?.margins || [0, 0, 0, 0]);
  };

  const setZoomAndCenter = (center: IMapPoint, zoom: number) => {
    const instanceMap = mapRef.current.map;
    instanceMap.setZoomAndCenter(zoom, [center.lng, center.lat], true);
  }

  const setCenter = (center: { lng: number; lat: number }) => {
    const instanceMap = mapRef.current?.map;
    if(instanceMap) {
      instanceMap.setCenter([center.lng, center.lat], true, 0);
    }
  };

  const createPoint = (item: { lng: number; lat: number }) => {
    const Point = new AMap.LngLat(item.lng, item.lat);
    return Point;
  };

  const createPolyline = (
    path: [{ lng: number; lat: number }],
    options: AMap.PolylineOptions,
  ) => {
    const Polyline = new AMap.Polyline({
      path,
      ...options,
    });
    return Polyline;
  };

  const getLocation = async (position: IMapPoint) => {
    return new Promise((resolve, reject) => {
      const geocoder: any = new AMap.Geocoder({
        radius: 1000, //以已知坐标为中心点，radius为半径，返回范围内兴趣点和道路信息
        extensions: 'all', //返回地址描述以及附近兴趣点和道路信息，默认“base”
      });
      geocoder.getAddress([position.lng, position.lat], function(
        status: string,
        result: any,
      ) {
        if (status === 'complete') {
          const { formattedAddress } = result?.regeocode;
          resolve({ address: formattedAddress, title: '' });
        } else {
          reject();
        }
      });
    });
  };

  const getOverlays = () => {
    const instanceMap = mapRef.current.map;
    const allOverlays = instanceMap.getAllOverlays();
    return allOverlays;
  };

  const addOverlay = (overlay: AMap.Overlay) => {
    const instanceMap = mapRef.current.map;
    instanceMap.add(overlay);
  };

  const clearOverlays = () => {
    const instanceMap = mapRef.current.map;
    const allOverlays = instanceMap.getAllOverlays();
    if(allOverlays?.length) {
      instanceMap.clearMap();
    }
  };

  const clearOverlayByType = (type?: string) => {
    const instanceMap = mapRef.current.map;
    const overlays = getOverlays();
    if(!overlays?.length) {
      return;
    }
    if(type === 'Polyline') {
      const tempOverlays = overlays.filter((i: any) => i.className === 'Overlay.Polyline');
      if(tempOverlays?.length) {
        instanceMap.remove(tempOverlays);
      }
    }
    if(type === 'Polygon') {
      const tempOverlays = overlays.filter((i: any) => i.className === 'Overlay.Polygon');
      if(tempOverlays?.length) {
        instanceMap.remove(tempOverlays);
      }
    }
  }

  const removeOverlay = (overlay: AMap.Overlay) => {
    const instanceMap = mapRef.current.map;
    instanceMap.remove(overlay);
  };

  const getZoom = () => {
    const instanceMap = mapRef.current.map;
    return instanceMap.getZoom();
  };

  const setZoom = (zoom: number) => {
    const instanceMap = mapRef.current.map;
    return instanceMap.setZoom(zoom);
  }

  const onLoad = () => {
    // console.log('load map 0');
    if (!isFirstLoaded) {
      setFirstLoaded(true);
      // console.log('load map 00')
      props.onMapLoad && props.onMapLoad();
    }
  };

  const zoomIn = () => {
    const instanceMap = mapRef.current.map;
    if (instanceMap) {
      instanceMap.setZoom(getZoom() + 1);
    }
  };
  const zoomOut = () => {
    const instanceMap = mapRef.current.map;
    if (instanceMap) {
      instanceMap.setZoom(getZoom() - 1);
    }
  };
  // console.log(zoom, 'zoom')

  return (
    <APILoader
      akay={AMAPKEY}
      version="2.0"
      plugin="AMap.Geocoder,AMap.PlaceSearch,AMap.MouseTool"
    >
      <Map
        // maxZoom={ maxZoom || 19 }
        // minZoom={ minZoom || 3 }
        zooms={ [minZoom || 3, maxZoom || 18]}
        center={mapCenter}
        zoom={zoom || 18}
        ref={mapRef}
        onComplete={onLoad}
        // zoomEnable={enablePinchToZoom}
        scrollWheel={enablePinchToZoom}
        touchZoom={enablePinchToZoom}
        // enablePinchToZoom={ enablePinchToZoom }
        // enableDoubleClickZoom={ enableDoubleClickZoom }
        // enableHighResolution={ true }
        onZoomEnd={onZoomEnd}
        onMoveStart={onTouchStart}
        doubleClickZoom={enableDoubleClickZoom}
      >
        {props.children}
      </Map>
    </APILoader>
  );
};

export default forwardRef(AMapContent);
